/** @jsxImportSource @emotion/react */
import { useRef, useState, type MouseEvent, type ReactNode } from 'react';
import {
  chartStyle,
  plotStyle,
  gridLineStyle,
  gridLabelStyle,
  barsRowStyle,
  colStyle,
  barOuterStyle,
  barStackStyle,
  barGrowStyle,
  segStyle,
  tooltipStyle,
  tooltipTitleStyle,
  tooltipRowStyle,
  tooltipCaptionStyle,
  tooltipDotStyle,
  tooltipValueStyle,
  emptyStyle,
} from './PopulationChart.styles';

export interface StackSegment {
  key: string;
  value: number;
}

export interface PopulationBar {
  label: string;
  segments: StackSegment[];
}

export interface PopulationChartProps {
  data: PopulationBar[];
  /** 세그먼트 key → 색. tokens.ts chart 팔레트 기반. */
  colorOf: (key: string) => string;
}

const TICKS = 4;

/** 커서 위에 즉시 뜨는 커스텀 툴팁 상태. */
interface TipState {
  x: number;
  y: number;
  content: ReactNode;
}

/** 날짜별(또는 1단계 그룹별) population을 스택 세로 막대로 표시하는 차트. */
export function PopulationChart({ data, colorOf }: PopulationChartProps) {
  // 툴팁 좌표는 차트 래퍼 기준(상대). 래퍼 ref로 clientXY → 상대좌표 변환.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<TipState | null>(null);

  const showTip = (content: ReactNode) => (e: MouseEvent) => {
    // 세그먼트→막대 순으로 버블링되므로, 더 구체적인 쪽이 먼저 잡고 멈춘다.
    e.stopPropagation();
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    // 좌우 끝에서 툴팁이 잘리지 않게 x를 래퍼 폭 안으로 clamp.
    const x = Math.min(Math.max(e.clientX - rect.left, 4), rect.width - 4);
    setTip({ x, y: e.clientY - rect.top, content });
  };
  const hideTip = () => setTip(null);

  if (data.length === 0) {
    return <div css={emptyStyle}>표시할 데이터가 없습니다.</div>;
  }

  const totals = data.map((d) => d.segments.reduce((s, seg) => s + seg.value, 0));
  // 막대 높이·grid 기준 = 실제 최댓값(0 나누기 방지). 가장 큰 막대가 top grid 에 닿는다.
  const scaleMax = Math.max(...totals, 1);

  // y축 grid: 0 ~ scaleMax 를 TICKS 등분.
  const ticks = Array.from({ length: TICKS + 1 }, (_, i) => {
    const frac = i / TICKS;
    return { pct: frac * 100, value: scaleMax * frac };
  });

  // 데이터 구성(라벨·세그먼트 키) 시그니처. 그룹 기준(pri/sec)이 바뀌면
  // 값이 달라져 이 키가 변하고, 막대 행이 remount 되며 자라나는 애니메이션이 재생된다.
  const animKey = data
    .map((d) => `${d.label}:${d.segments.map((s) => s.key).join(',')}`)
    .join('|');

  return (
    <div css={chartStyle} ref={wrapRef}>
      <div css={plotStyle}>
        {/* y축 grid: 값 라벨 + 가로 점선 */}
        {ticks.map((t) => (
          <div key={t.pct} css={gridLineStyle} style={{ bottom: `${t.pct}%` }}>
            <span css={gridLabelStyle}>
              {t.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          </div>
        ))}

        {/* 막대들 — animKey 변경 시 remount 되어 애니메이션 재생 */}
        <div css={barsRowStyle} key={animKey}>
          {data.map((bar, i) => {
            const total = totals[i];
            const stacked = bar.segments.length > 1;
            return (
              <div key={bar.label} css={colStyle}>
                <div
                  css={barOuterStyle}
                  style={{ height: `${(total / scaleMax) * 100}%` }}
                  // x축 라벨은 상시 표시하지 않고 커스텀 hover 툴팁으로만.
                  onMouseMove={showTip(
                    <>
                      <span css={tooltipTitleStyle}>{bar.label}</span>
                      <div css={tooltipRowStyle}>
                        <span css={tooltipCaptionStyle}>건수</span>
                        <span css={tooltipValueStyle}>{total.toLocaleString()}</span>
                      </div>
                    </>
                  )}
                  onMouseLeave={hideTip}
                >
                  <div
                    css={[barStackStyle, barGrowStyle]}
                    // 막대마다 조금씩 늦게 시작해 순차로 자라나는 느낌.
                    style={{ animationDelay: `${Math.min(i * 20, 400)}ms` }}
                  >
                    {bar.segments.map((seg) => (
                      <div
                        key={seg.key}
                        css={segStyle}
                        style={{
                          // 막대 내 세그먼트 비율. total 0이면 세그먼트도 0.
                          height: total > 0 ? `${(seg.value / total) * 100}%` : '0%',
                          background: colorOf(seg.key),
                        }}
                        // 스택일 때만 세그먼트별 상세; 단일이면 막대(total) 툴팁에 위임.
                        onMouseMove={
                          stacked
                            ? showTip(
                                <>
                                  <span css={tooltipTitleStyle}>{bar.label}</span>
                                  <div css={tooltipRowStyle}>
                                    <span css={tooltipCaptionStyle}>
                                      <span css={tooltipDotStyle(colorOf(seg.key))} />
                                      {seg.key}
                                    </span>
                                    <span css={tooltipValueStyle}>
                                      {seg.value.toLocaleString()}
                                    </span>
                                  </div>
                                </>
                              )
                            : undefined
                        }
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 커서 위에 즉시 뜨는 커스텀 툴팁 */}
      {tip && (
        <div css={tooltipStyle} style={{ left: tip.x, top: tip.y }}>
          {tip.content}
        </div>
      )}
    </div>
  );
}
